import { useState, useEffect } from 'react';
import type { Product } from '../../types/repricer';
import { validateProducts } from '../../utils/repricer/dataValidation';
import './DataLoader.css';

interface DataLoaderProps {
  dataUrl: string;
  onDataLoaded: (data: any[], timestamp?: string) => void;
  children?: React.ReactNode;
}

interface DataLoaderState {
  loading: boolean;
  error: string | null;
  data: any[] | null;
  lastUpdated?: string;
}

/**
 * DataLoader component handles loading and parsing JSON product data
 */
export function DataLoader({ dataUrl, onDataLoaded, children }: DataLoaderProps) {
  const [state, setState] = useState<DataLoaderState>({
    loading: true,
    error: null,
    data: null,
    lastUpdated: undefined,
  });

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        setState({ loading: true, error: null, data: null, lastUpdated: undefined });

        let data: any[] = [];
        let timestamp = new Date().toISOString();

        if (dataUrl === 'graphql') {
          // Fetch from GraphQL endpoint
          const response = await fetch('https://le3mt7znejbrfocvtg2646uwwm.appsync-api.us-east-1.amazonaws.com/graphql', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': 'da2-bucz5zsxffg65hn5wyoc5bzpba'
            },
            body: JSON.stringify({
              query: 'query { getRepricerDashboard }'
            })
          });
          
          if (!response.ok) {
            throw new Error(`Failed to load data: ${response.status} ${response.statusText}`);
          }

          const result = await response.json();
          const dashboardData = JSON.parse(result.data.getRepricerDashboard);
          
          // Extract data and timestamp
          data = dashboardData;
          timestamp = dashboardData.lastUpdated || new Date().toISOString();
        } else {
          // Fetch from static file
          const response = await fetch(dataUrl);
          
          if (!response.ok) {
            throw new Error(`Failed to load data: ${response.status} ${response.statusText}`);
          }

          const rawData = await response.json();
          data = validateProducts(rawData);

          if (data.length === 0) {
            throw new Error('No valid products found in data file');
          }
        }

        if (isMounted) {
          setState({ loading: false, error: null, data: data, lastUpdated: timestamp });
          onDataLoaded(data, timestamp);
        }
      } catch (err) {
        if (isMounted) {
          const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
          setState({ loading: false, error: errorMessage, data: null, lastUpdated: undefined });
          console.error('Error loading data:', err);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [dataUrl, onDataLoaded]);

  if (state.loading) {
    return (
      <div className="data-loader-container" role="status" aria-live="polite">
        <div className="loading-spinner">
          <div className="spinner" aria-hidden="true"></div>
          <p>Loading product data...</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="data-loader-container" role="alert" aria-live="assertive">
        <div className="error-message">
          <h2>Error Loading Data</h2>
          <p>{state.error}</p>
          <button onClick={() => window.location.reload()} aria-label="Retry loading data">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
